import type { FC, ReactNode } from 'react'

import { useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { createSilkField } from '@/lib/dots/fields'
import { DotCanvas } from '@/components/dots/dot-canvas'

interface SilkDriftProps {
    className?: string
}

type Placement = {
    id: number
    side: 'left' | 'right'
    top: number
    offset: number
    size: number
    mirror: boolean
    tilt: number
    seed: number
    speed: number
    startTime: number
}

const between = (min: number, max: number): number => min + Math.random() * (max - min)

// A random spot hugging the left or right edge, so the ribbon drifts through the margins rather than over the copy.
const randomPlacement = (id: number): Placement => ({
    id,
    side: Math.random() < 0.5 ? 'left' : 'right',
    top: between(14, 86),
    offset: between(-28, -6),
    size: between(36, 54),
    mirror: Math.random() < 0.5,
    tilt: between(-0.35, 0.35),
    seed: Math.floor(between(1, 9999)),
    speed: between(0.7, 1.15),
    startTime: between(0, 40)
})

// Copies of the hero's silk ribbon that fade in and out at random places inside a section.
// Placement is picked on the client after mount, so the server and the first client render match.
export const SilkDrift: FC<SilkDriftProps> = ({ className }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const [placement, setPlacement] = useState<Placement | null>(null)
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        const element = ref.current

        if (!element) return

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setPlacement(randomPlacement(0))
            setVisible(true)
            return
        }

        let timer = 0
        let active = false
        let count = 0

        const later = (callback: () => void, ms: number): void => {
            window.clearTimeout(timer)
            timer = window.setTimeout(callback, ms)
        }

        const hide = (): void => {
            setVisible(false)

            // Unmount once the fade has finished so a hidden ribbon stops drawing.
            later(() => {
                setPlacement(null)
                later(appear, between(1200, 5000))
            }, 1500)
        }

        const appear = (): void => {
            // Sometimes skip a turn so the ribbons feel unpredictable rather than on a loop.
            if (count > 0 && Math.random() < 0.3) {
                setPlacement(null)
                later(appear, between(2500, 5000))
                return
            }

            count += 1
            setPlacement(randomPlacement(count))
            later(() => {
                setVisible(true)
                later(hide, between(6500, 11000))
            }, 180)
        }

        const observer = new IntersectionObserver(entries => {
            const entry = entries[0]

            if (!entry) return

            if (entry.isIntersecting && !active) {
                active = true
                later(appear, between(1500, 3200))
            }
            else if (!entry.isIntersecting && active) {
                active = false
                setVisible(false)
                later(() => setPlacement(null), 1500)
            }
        }, { rootMargin: '-10% 0px' })

        observer.observe(element)

        return () => {
            observer.disconnect()
            window.clearTimeout(timer)
        }
    }, [])

    const field = useMemo(
        () => (placement ? createSilkField(placement.seed, placement.tilt) : null),
        [placement]
    )

    return (
        <div ref={ref} aria-hidden='true' className={cn('pointer-events-none absolute inset-0 -z-10 overflow-hidden', className)}>
            {placement && field && (
                <div
                    key={placement.id}
                    className={cn(
                        'silk-drift absolute aspect-square -translate-y-1/2 transition-opacity duration-[1400ms] ease-out',
                        visible ? 'opacity-60 md:opacity-80' : 'opacity-0'
                    )}
                    style={{
                        top: `${placement.top}%`,
                        left: placement.side === 'left' ? `${placement.offset}%` : undefined,
                        right: placement.side === 'right' ? `${placement.offset}%` : undefined,
                        width: `clamp(260px, ${placement.size}vw, 720px)`
                    }}
                >
                    <div className={cn('size-full', placement.mirror && '-scale-x-100')}>
                        <DotCanvas
                            field={field}
                            scale={0.62}
                            maxCells={12000}
                            speed={placement.speed}
                            startTime={placement.startTime}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}
